import React, { useState, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Grid,
  Box,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Autocomplete,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  ListAlt as ListAltIcon,
} from "@mui/icons-material";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";

import TableLoteFinanciera from "./TableLoteFinanciera";

const colors = {
  primary: "#1e293b",
  secondary: "#0b4f6c",
  white: "#ffffff",
  success: "#27ae60",
  warning: "#da1c15ff",
  info: "#2980b9",
};

const HeaderLoteFinanciera = ({ refreshFlag, onError, onSuccess }) => {
  const [selectedConciliationId, setSelectedConciliationId] = useState("");

  const [llaveSeleccionada, setLlaveSeleccionada] = useState(null);
  const [llaveOptions, setLlaveOptions] = useState([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");

  // Estados para búsqueda con debounce
  const [busquedaGlobal, setBusquedaGlobal] = useState("");
  const [busquedaDebounced, setBusquedaDebounced] = useState("");
  const [tipoFiltro, setTipoFiltro] = useState("global");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [statusOptions, setStatusOptions] = useState([]);
  const handleStatusOptionsChange = useCallback((options) => {
    setStatusOptions(options);
    setFiltroStatus((current) =>
      current === "todos" || options.some((option) => option.value === current) ? current : "todos"
    );
  }, []);
  const [filtrosActivos, setFiltrosActivos] = useState(false);

  // Ref para el timeout del debounce
  const debounceTimeout = useRef(null);

  const handleKeyOptionsChange = useCallback(({ options, loading }) => {
    setLoadingKeys(loading);
    if (!loading) setLlaveOptions(options);
  }, []);

  // ================================================
  // DEBOUNCE - Espera antes de actualizar la búsqueda
  // ================================================
  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    if (busquedaGlobal === "") {
      setBusquedaDebounced("");
      return;
    }

    debounceTimeout.current = setTimeout(() => {
      setBusquedaDebounced(busquedaGlobal);
    }, 1000);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [busquedaGlobal]);

  // Verificar si hay filtros activos
  useEffect(() => {
    const hasFilters = !!(
      llaveSeleccionada ||
      (!llaveSeleccionada && (fechaInicio || fechaFin)) ||
      busquedaDebounced ||
      filtroStatus !== "todos"
    );
    setFiltrosActivos(hasFilters);
  }, [llaveSeleccionada, fechaInicio, fechaFin, busquedaDebounced, filtroStatus]);

  // ✅ Recibe la llave desde el botón "Filtrar" de la tabla
  // y la selecciona en el Autocomplete (esto dispara el fetch por llave)
  const handleFiltrarLlave = useCallback(
    (key) => {
      const value = String(key || "").trim();
      if (!value) return;

      const opcion = llaveOptions.find((opt) => String(opt.value) === value);
      if (!opcion) return;

      // Limpiar la búsqueda de texto para que no oculte los resultados por llave
      setBusquedaGlobal("");
      setBusquedaDebounced("");

      setFiltroStatus("todos");
      setLlaveSeleccionada(opcion);
    },
    [llaveOptions]
  );

  // Limpiar todos los filtros
  const clearFilters = () => {
    setLlaveSeleccionada(null);
    setFechaInicio("");
    setFechaFin("");
    setBusquedaGlobal("");
    setBusquedaDebounced("");
    setFiltroStatus("todos");
    setFiltrosActivos(false);
  };

  // Manejar búsqueda con Enter - forzar actualización inmediata
  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      setBusquedaDebounced(busquedaGlobal);
    }
  };

  return (
    <MDBox position="relative" sx={{ mt: -2, mx: -3, py: 2, px: -2 }}>
      <Card
        sx={{
          mt: 1,
          mx: 3,
          py: 3,
          px: 3,
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #1e293b 0%, #0b4f6c 100%)",
            margin: -3,
            marginBottom: 3,
            padding: 3,
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={2}>
                <ListAltIcon sx={{ fontSize: 32, color: "rgba(255,255,255,0.8)" }} />
                <Box>
                  <MDTypography
                    variant="h4"
                    fontWeight="bold"
                    sx={{ color: "rgba(255, 255, 255)" }}
                  >
                    Conciliaciones Financieras
                  </MDTypography>
                  <MDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", mt: 0.5 }}>
                    Administre las conciliaciones financieras del sistema
                  </MDTypography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                {filtrosActivos && (
                  <Chip
                    label="Filtros activos"
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.15)",
                      color: "rgba(255,255,255,0.8)",
                    }}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Filtros */}
        <Box sx={{ mb: 3, p: 2, bgcolor: "#f8fafc", borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Búsqueda Global con indicador de debounce */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                placeholder="Buscar por ID, Nombre o Llave..."
                value={busquedaGlobal}
                onChange={(e) => setBusquedaGlobal(e.target.value)}
                onKeyPress={handleKeyPress}
                size="small"
                sx={{
                  "& .MuiInputBase-root": {
                    height: "44px",
                    backgroundColor: "#ffffff",
                    borderRadius: "8px",
                  },
                  "& input": {
                    padding: "8px 16px",
                    fontSize: "0.9rem",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#64748b" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {busquedaGlobal && busquedaGlobal !== busquedaDebounced && (
                        <Chip
                          label="Buscando..."
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.65rem",
                            bgcolor: "#fff3e0",
                            color: "#e65100",
                            animation: "pulse 1s infinite",
                            "@keyframes pulse": {
                              "0%": { opacity: 1 },
                              "50%": { opacity: 0.5 },
                              "100%": { opacity: 1 },
                            },
                          }}
                        />
                      )}
                      {busquedaGlobal && (
                        <Button
                          size="small"
                          onClick={() => {
                            setBusquedaGlobal("");
                            setBusquedaDebounced("");
                          }}
                          sx={{
                            minWidth: "auto",
                            p: 0.5,
                            color: "#64748b",
                            "&:hover": { color: "#d32f2f" },
                          }}
                        >
                          <ClearIcon fontSize="small" />
                        </Button>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Filtros adicionales */}
            <Grid item xs={12}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    options={llaveOptions}
                    loading={loadingKeys}
                    value={llaveSeleccionada}
                    onChange={(event, newValue) => {
                      setFiltroStatus("todos");
                      setLlaveSeleccionada(newValue);
                    }}
                    getOptionLabel={(option) => option?.label || ""}
                    isOptionEqualToValue={(option, value) =>
                      String(option?.value) === String(value?.value)
                    }
                    renderInput={(params) => {
                      const { InputLabelProps, InputProps, ...restParams } = params;
                      return (
                        <TextField
                          {...restParams}
                          label="Llave de Conciliación"
                          placeholder="Filtrar por llave..."
                          size="small"
                          sx={{ "& .MuiInputBase-root": { height: "40px" } }}
                          InputLabelProps={InputLabelProps}
                          InputProps={{
                            ...InputProps,
                            endAdornment: (
                              <>
                                {loadingKeys ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      );
                    }}
                    renderOption={({ key, ...optionProps }, option) => (
                      <Box component="li" {...optionProps} key={option.value ?? key}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={option.label}
                            size="small"
                            sx={{
                              fontFamily: "monospace",
                              backgroundColor: `${colors.info}15`,
                              color: colors.info,
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                    sx={{
                      "& .MuiAutocomplete-inputRoot": {
                        height: "40px",
                        padding: "0 14px",
                      },
                    }}
                  />
                </Grid>

                {!llaveSeleccionada && (
                  <>
                    <Grid item xs={6} md={2}>
                      <TextField
                        fullWidth
                        label="Creación desde"
                        type="date"
                        inputProps={{ max: fechaFin || undefined }}
                        value={fechaInicio}
                        onChange={(e) => setFechaInicio(e.target.value)}
                        size="small"
                        sx={{
                          "& .MuiInputBase-root": { height: "40px" },
                          "& input": { padding: "8px 12px" },
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid item xs={6} md={2}>
                      <TextField
                        fullWidth
                        label="Creación hasta"
                        type="date"
                        inputProps={{ min: fechaInicio || undefined }}
                        error={Boolean(fechaInicio && fechaFin && fechaInicio > fechaFin)}
                        helperText={
                          fechaInicio && fechaFin && fechaInicio > fechaFin
                            ? "Revise el rango de fechas"
                            : ""
                        }
                        value={fechaFin}
                        onChange={(e) => setFechaFin(e.target.value)}
                        size="small"
                        sx={{
                          "& .MuiInputBase-root": { height: "40px" },
                          "& input": { padding: "8px 12px" },
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Estado"
                    value={filtroStatus}
                    onChange={(event) => setFiltroStatus(event.target.value)}
                    sx={{ "& .MuiInputBase-root": { minHeight: 40 } }}
                  >
                    <MenuItem value="todos">Todos los estados</MenuItem>
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} md={1.5}>
                  {filtrosActivos && (
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      onClick={clearFilters}
                      startIcon={<ClearIcon />}
                      sx={{
                        height: "40px",
                        textTransform: "none",
                        color: "#d32f2f",
                        borderColor: "#d32f2f",
                        "&:hover": {
                          borderColor: "#b71c1c",
                          backgroundColor: "rgba(211, 47, 47, 0.04)",
                        },
                      }}
                    >
                      Limpiar todo
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Resumen de filtros aplicados */}
          {filtrosActivos && (
            <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
              {busquedaDebounced && (
                <Chip
                  label={`Búsqueda: "${busquedaDebounced}"`}
                  size="small"
                  onDelete={() => {
                    setBusquedaGlobal("");
                    setBusquedaDebounced("");
                  }}
                  sx={{
                    bgcolor: `${colors.info}15`,
                    color: colors.info,
                    maxWidth: "300px",
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              )}
              {llaveSeleccionada && (
                <Chip
                  label={`Llave: ${llaveSeleccionada.label}`}
                  size="small"
                  onDelete={() => setLlaveSeleccionada(null)}
                  sx={{
                    bgcolor: `${colors.info}15`,
                    color: colors.info,
                    fontFamily: "monospace",
                  }}
                />
              )}
              {filtroStatus !== "todos" && (
                <Chip
                  label={`Estado: ${
                    statusOptions.find((o) => o.value === filtroStatus)?.label || filtroStatus
                  }`}
                  size="small"
                  onDelete={() => setFiltroStatus("todos")}
                  sx={{
                    bgcolor:
                      filtroStatus === "2"
                        ? `${colors.success}25`
                        : filtroStatus === "3"
                        ? `${colors.info}25`
                        : filtroStatus === "1"
                        ? `${colors.warning}25`
                        : `${colors.warning}15`,
                    color:
                      filtroStatus === "2"
                        ? colors.success
                        : filtroStatus === "3"
                        ? colors.info
                        : filtroStatus === "1"
                        ? colors.warning
                        : colors.warning,
                  }}
                />
              )}
              {!llaveSeleccionada && fechaInicio && (
                <Chip
                  label={`Creación desde: ${fechaInicio}`}
                  size="small"
                  onDelete={() => setFechaInicio("")}
                  sx={{ bgcolor: "#e3f2fd", color: "#0b4f6c" }}
                />
              )}
              {!llaveSeleccionada && fechaFin && (
                <Chip
                  label={`Creación hasta: ${fechaFin}`}
                  size="small"
                  onDelete={() => setFechaFin("")}
                  sx={{ bgcolor: "#e3f2fd", color: "#0b4f6c" }}
                />
              )}
            </Box>
          )}
        </Box>

        {/* Tabla de Conciliaciones */}
        <Box sx={{ mt: 2 }}>
          <TableLoteFinanciera
            refreshFlag={refreshFlag}
            onError={onError}
            onSelectLote={setSelectedConciliationId}
            onFiltrarLlave={handleFiltrarLlave}
            llaveSeleccionada={llaveSeleccionada?.value || ""}
            estadoLoteSeleccionado={llaveSeleccionada?.status || ""}
            fechaInicio={llaveSeleccionada ? "" : fechaInicio}
            fechaFin={llaveSeleccionada ? "" : fechaFin}
            filtroVertical=""
            filtroBusqueda={busquedaDebounced}
            filtroStatus={filtroStatus}
            onStatusOptionsChange={handleStatusOptionsChange}
            onKeyOptionsChange={handleKeyOptionsChange}
            tipoFiltro="global"
          />
        </Box>
      </Card>
    </MDBox>
  );
};

HeaderLoteFinanciera.propTypes = {
  refreshFlag: PropTypes.bool,
  onError: PropTypes.func,
  onSuccess: PropTypes.func,
};

HeaderLoteFinanciera.defaultProps = {
  refreshFlag: false,
  onError: null,
  onSuccess: null,
};

export default HeaderLoteFinanciera;
